import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { serviceURL } from '@/services/ServiceConstants';
import balloonGlow from './assets/balloon-glow.svg';
import balloon from './assets/balloon.svg';
import bezel from './assets/bezel.webp';
import cloudA from './assets/cloud-a.svg';
import cloudB from './assets/cloud-b.svg';
import cloudC from './assets/cloud-c.svg';
import wordmark from './assets/codify-wordmark.svg';
import headline from './assets/get-early-access.svg';
import phoneScreen from './assets/phone-screen.svg';
import scene from './assets/scene.svg';
import starField from './assets/stars.json';
import './LandingPage.css';
import { PrivacyPolicy } from './PrivacyPolicy';
import './PrivacyPolicy.css';

type SubmitState = 'idle' | 'submitting' | 'joined' | 'error';

type AtmosphereDepth = 1 | 2 | 3 | 4 | 5;
const AtmosphereDepths: ReadonlyArray<AtmosphereDepth> = [1, 2, 3, 4, 5];

type AtmosphereSprite = {
  source: string;
  width: number;
  top: number;
  depth: AtmosphereDepth;
  duration: number;
  delay: number;
  reverse?: boolean;
};

const CloudSprites: ReadonlyArray<AtmosphereSprite> = [
  { source: cloudA, width: 190, top: 62, depth: 5, duration: 110, delay: -12 },
  {
    source: cloudB,
    width: 140,
    top: 68,
    depth: 3,
    duration: 145,
    delay: -58,
    reverse: true,
  },
  { source: cloudC, width: 165, top: 74, depth: 4, duration: 125, delay: -83 },
  { source: cloudB, width: 95, top: 60, depth: 1, duration: 180, delay: -35 },
  {
    source: cloudC,
    width: 115,
    top: 79,
    depth: 2,
    duration: 165,
    delay: -72,
    reverse: true,
  },
];

const BalloonSprites: ReadonlyArray<Omit<AtmosphereSprite, 'source'>> = [
  { width: 30, top: 60, depth: 5, duration: 180, delay: -8 },
  { width: 22, top: 70, depth: 3, duration: 225, delay: -67 },
  { width: 17, top: 78, depth: 1, duration: 270, delay: -125 },
  { width: 26, top: 57, depth: 4, duration: 200, delay: -159 },
  { width: 19, top: 73, depth: 2, duration: 250, delay: -201 },
];

function atmosphereOpacity(depth: AtmosphereDepth): number {
  return 0.52 + depth * 0.085;
}

function atmosphereShadow(depth: AtmosphereDepth): string {
  const offset = 1 + depth;
  const blur = 5 + depth * 2;
  const opacity = 0.1 + depth * 0.025;
  return `drop-shadow(0 ${offset}px ${blur}px rgb(60 70 120 / ${opacity}))`;
}

type BalloonState = {
  active: boolean;
  x: number;
  y: number;
  direction: number;
  baseY: number;
  speed: number;
  phase: number;
  lift: number;
  velocityY: number;
  wobble: number;
  glow: number;
  nextSpawn: number;
};

const ForegroundBalloon = memo(function ForegroundBalloon({
  cheerSignal,
}: {
  cheerSignal: number;
}) {
  const balloonRef = useRef<HTMLButtonElement>(null);
  const glowRef = useRef<HTMLImageElement>(null);
  const stateRef = useRef<BalloonState>({
    active: false,
    x: 0,
    y: 0,
    direction: 1,
    baseY: 0,
    speed: 30,
    phase: 0,
    lift: 0,
    velocityY: 0,
    wobble: 1,
    glow: 0,
    nextSpawn: 3,
  });

  const spawnBalloon = useCallback((time: number) => {
    const state = stateRef.current;
    const element = balloonRef.current;
    const glow = glowRef.current;
    if (element === null || glow === null) {
      return;
    }

    state.active = true;
    state.direction = Math.random() < 0.5 ? 1 : -1;
    state.x = state.direction > 0 ? -150 : window.innerWidth + 150;
    state.baseY = window.innerHeight * (0.56 + Math.random() * 0.16);
    state.speed = 26 + Math.random() * 18;
    state.phase = Math.random() * Math.PI * 2;
    state.lift = 0;
    state.velocityY = 0;
    state.wobble = 1;
    state.glow = 0;
    state.y = state.baseY + Math.sin(time * 0.45 + state.phase) * 14;

    element.style.display = 'block';
    element.style.transform = `translate(${state.x - 42}px, ${state.y - 45}px)`;
    glow.style.opacity = '0';
  }, []);

  const boostBalloon = useCallback(() => {
    const state = stateRef.current;
    if (!state.active) {
      return;
    }
    state.velocityY -= 140;
    state.wobble = 0;
    state.glow = 1;
  }, []);

  useEffect(() => {
    if (cheerSignal === 0) {
      return;
    }
    if (!stateRef.current.active) {
      spawnBalloon(performance.now() / 1000);
    }
    boostBalloon();
  }, [boostBalloon, cheerSignal, spawnBalloon]);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const state = stateRef.current;
    state.nextSpawn = reducedMotion ? Number.POSITIVE_INFINITY : 3;
    let animationFrame = 0;
    let lastTime = 0;

    const animate = (now: number) => {
      animationFrame = window.requestAnimationFrame(animate);
      const element = balloonRef.current;
      const glow = glowRef.current;
      if (element === null || glow === null) {
        return;
      }

      const time = now / 1000;
      const deltaTime = Math.min(0.05, time - lastTime);
      lastTime = time;
      if (document.hidden) {
        return;
      }
      if (!state.active) {
        if (time > state.nextSpawn) {
          spawnBalloon(time);
        }
        return;
      }

      state.x += state.direction * state.speed * deltaTime;
      state.y = state.baseY + Math.sin(time * 0.45 + state.phase) * 14;
      state.lift += state.velocityY * deltaTime;
      state.velocityY *= Math.exp(-1.5 * deltaTime);

      let rotation = Math.sin(time * 0.7 + state.phase) * 3;
      if (state.wobble < 1) {
        state.wobble = Math.min(1, state.wobble + deltaTime / 1.2);
        rotation +=
          Math.sin(state.wobble * Math.PI * 3) * 7 * (1 - state.wobble);
      }
      element.style.transform = `translate(${state.x - 42}px, ${state.y + state.lift - 45}px) rotate(${rotation}deg)`;

      state.glow = Math.max(0, state.glow - deltaTime / 0.7);
      glow.style.opacity = String(
        state.glow * (0.85 + Math.sin(time * 37) * 0.15),
      );

      if (
        state.x < -180 ||
        state.x > window.innerWidth + 180 ||
        state.y + state.lift < -160
      ) {
        state.active = false;
        element.style.display = 'none';
        state.nextSpawn = time + 8 + Math.random() * 10;
      }
    };

    animationFrame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [spawnBalloon]);

  return (
    <button
      ref={balloonRef}
      className="hero-balloon"
      type="button"
      aria-label="Boost hot air balloon"
      onClick={boostBalloon}
    >
      <img className="balloon-art" src={balloon} alt="" draggable={false} />
      <img
        ref={glowRef}
        className="balloon-glow"
        src={balloonGlow}
        alt=""
        draggable={false}
      />
    </button>
  );
});

export function LandingPage() {
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [balloonCheers, setBalloonCheers] = useState(0);
  const [showPrivacy, setShowPrivacy] = useState(() =>
    window.location.pathname.startsWith('/privacy'),
  );
  const starsRef = useRef<SVGSVGElement>(null);
  const starElementsRef = useRef<Array<SVGCircleElement | null>>([]);
  const stars = useMemo(
    () =>
      starField.dots.map(([x = 0, y = 0, alpha = 0], index) => ({
        x,
        y,
        opacity: alpha / 255,
        radius: 1.1 + ((index * 40503) % 997) / 997,
        phase: ((index * 2654435761) % 1000) / 1000,
        ramp: 1.2 + (((index * 2246822519) % 991) / 991) * 3.2,
        glow: 0,
        twinkle: 0,
        screenX: 0,
        screenY: 0,
        lastOpacity: '',
        lastRadius: '',
      })),
    [],
  );

  useEffect(() => {
    document.documentElement.classList.add('landing-active');
    document.body.classList.add('landing-active');
    return () => {
      document.documentElement.classList.remove('landing-active');
      document.body.classList.remove('landing-active');
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setShowPrivacy(window.location.pathname.startsWith('/privacy'));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    document.title = showPrivacy
      ? 'Privacy Policy — Codify'
      : 'Codify — iOS apps, just by chatting';
  }, [showPrivacy]);

  const openPrivacy = useCallback(() => {
    window.history.pushState(null, '', '/privacy');
    setShowPrivacy(true);
  }, []);

  const closePrivacy = useCallback(() => {
    window.history.pushState(null, '', '/');
    setShowPrivacy(false);
  }, []);

  const starLayer = useMemo(
    () => (
      <svg
        ref={starsRef}
        className="landing-stars"
        aria-hidden="true"
        viewBox={`0 0 ${starField.W} ${starField.H}`}
        preserveAspectRatio="xMidYMid slice"
      >
        {stars.map((star, index) => (
          <circle
            key={index}
            ref={(element) => {
              starElementsRef.current[index] = element;
            }}
            cx={star.x}
            cy={star.y}
            r={star.radius}
            fillOpacity={star.opacity}
          />
        ))}
      </svg>
    ),
    [stars],
  );

  useEffect(() => {
    const pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reduceTwinkle = reducedMotion.matches;
    let animationFrame = 0;
    let lastTime = 0;
    let frameCount = 0;
    let twinkleStride = 1;
    let expensiveFrames = 0;
    let inexpensiveFrames = 0;

    let layoutFrame = 0;
    const updateLayout = () => {
      const svg = starsRef.current;
      if (svg === null) {
        return;
      }

      const field = svg.getBoundingClientRect();
      const scale = Math.max(
        field.width / starField.W,
        field.height / starField.H,
      );
      const offsetX = field.left + (field.width - starField.W * scale) / 2;
      const offsetY = field.top + (field.height - starField.H * scale) / 2;

      stars.forEach((star) => {
        star.screenX = star.x * scale + offsetX;
        star.screenY = star.y * scale + offsetY;
        star.lastOpacity = '';
        star.lastRadius = '';
      });
    };
    const scheduleLayoutUpdate = () => {
      if (layoutFrame !== 0) {
        return;
      }
      layoutFrame = window.requestAnimationFrame(() => {
        layoutFrame = 0;
        updateLayout();
      });
    };

    const handlePointerMove = (event: PointerEvent) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
    };
    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reduceTwinkle = event.matches;
    };

    const animateStars = (now: number) => {
      animationFrame = window.requestAnimationFrame(animateStars);
      const time = now / 1000;
      const deltaTime = Math.min(0.05, time - lastTime);
      lastTime = time;
      if (document.hidden) {
        return;
      }

      const workStarted = performance.now();
      frameCount += 1;
      const ease = Math.min(1, deltaTime * 3.5);
      const updateTwinkle = !reduceTwinkle && frameCount % twinkleStride === 0;

      stars.forEach((star, index) => {
        const element = starElementsRef.current[index];
        if (element === null || element === undefined) {
          return;
        }

        const targetGlow = Math.exp(
          -Math.hypot(star.screenX - pointer.x, star.screenY - pointer.y) / 110,
        );
        star.glow += (targetGlow - star.glow) * ease;

        if (reduceTwinkle) {
          star.twinkle = 0;
        } else if (updateTwinkle) {
          star.twinkle =
            0.25 * Math.sin(time * (0.6 + star.phase) + star.phase * 9);
        }

        const opacity = Math.max(
          0,
          star.opacity + star.twinkle + star.glow * 0.6,
        ).toFixed(3);
        const radius = (star.radius + star.glow * star.ramp).toFixed(2);
        if (opacity !== star.lastOpacity) {
          element.setAttribute('fill-opacity', opacity);
          star.lastOpacity = opacity;
        }
        if (radius !== star.lastRadius) {
          element.setAttribute('r', radius);
          star.lastRadius = radius;
        }
      });

      const workDuration = performance.now() - workStarted;
      const workBudget = Math.max(2, Math.min(6, deltaTime * 1000 * 0.35));
      if (workDuration > workBudget) {
        expensiveFrames += 1;
        inexpensiveFrames = 0;
        if (expensiveFrames >= 5 && twinkleStride < 4) {
          twinkleStride *= 2;
          expensiveFrames = 0;
        }
      } else if (workDuration < workBudget * 0.5) {
        inexpensiveFrames += 1;
        expensiveFrames = 0;
        if (inexpensiveFrames >= 120 && twinkleStride > 1) {
          twinkleStride /= 2;
          inexpensiveFrames = 0;
        }
      } else {
        expensiveFrames = 0;
        inexpensiveFrames = 0;
      }
    };

    window.addEventListener('pointermove', handlePointerMove, {
      passive: true,
    });
    window.addEventListener('resize', scheduleLayoutUpdate, { passive: true });
    reducedMotion.addEventListener('change', handleReducedMotionChange);
    updateLayout();
    animationFrame = window.requestAnimationFrame(animateStars);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('resize', scheduleLayoutUpdate);
      reducedMotion.removeEventListener('change', handleReducedMotionChange);
      window.cancelAnimationFrame(layoutFrame);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [stars]);

  async function joinWaitlist(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitState === 'submitting' || submitState === 'joined') {
      return;
    }
    setSubmitState('submitting');
    setBalloonCheers((value) => value + 1);

    try {
      const response = await fetch(
        serviceURL({ endpoint: 'register/waitlist' }),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        },
      );
      if (!response.ok) {
        throw new Error(`Waitlist request failed: ${response.status}`);
      }

      setSubmitState('joined');
    } catch {
      setSubmitState('error');
    }
  }

  return (
    <div className={`landing-page ${showPrivacy ? 'is-privacy' : ''}`}>
      <img
        className="landing-scene"
        src={scene}
        alt=""
        aria-hidden="true"
        draggable={false}
      />
      {starLayer}

      <div className="landing-atmosphere" aria-hidden="true">
        {AtmosphereDepths.map((depth) => (
          <div
            className={`atmosphere-plane ${depth <= 3 ? 'behind-far-ridge' : 'behind-near-ridge'}`}
            key={depth}
            data-depth={depth}
            style={{
              zIndex: depth * 10,
            }}
          >
            {CloudSprites.filter((cloud) => cloud.depth === depth).map(
              (cloud, index) => (
                <img
                  className={`landing-cloud ${cloud.reverse ? 'is-reverse' : ''}`}
                  src={cloud.source}
                  alt=""
                  key={`cloud-${index}`}
                  data-depth={cloud.depth}
                  style={{
                    top: `${cloud.top}%`,
                    width: cloud.width,
                    zIndex: 2,
                    opacity: atmosphereOpacity(cloud.depth),
                    filter: atmosphereShadow(cloud.depth),
                    animationDuration: `${cloud.duration}s`,
                    animationDelay: `${cloud.delay}s`,
                  }}
                />
              ),
            )}
            {BalloonSprites.filter(
              (miniBalloon) => miniBalloon.depth === depth,
            ).map((miniBalloon, index) => (
              <img
                className="mini-balloon"
                src={balloon}
                alt=""
                key={`balloon-${index}`}
                data-depth={miniBalloon.depth}
                draggable={false}
                style={{
                  top: `${miniBalloon.top}%`,
                  width: miniBalloon.width,
                  zIndex: 1,
                  opacity: atmosphereOpacity(miniBalloon.depth),
                  filter: atmosphereShadow(miniBalloon.depth),
                  animationDuration: `${miniBalloon.duration}s`,
                  animationDelay: `${miniBalloon.delay}s`,
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="landing-mountain-haze" aria-hidden="true" />
      <ForegroundBalloon cheerSignal={balloonCheers} />

      <img className="landing-wordmark" src={wordmark} alt="Codify" />
      <main className="landing-hero">
        <h1>
          <img src={headline} alt="Get early access" />
        </h1>
        <p className="landing-tagline">
          Get the iOS app out of your head, in seconds.
        </p>
        <p className="landing-copy">
          Codify builds fully functional mobile prototypes from your phone.
          Prompt, iterate, and explore your app ideas, with unlimited free alpha
          access.
        </p>

        <form
          className={`waitlist-form ${submitState === 'joined' ? 'is-joined' : ''}`}
          onSubmit={joinWaitlist}
        >
          {submitState === 'joined' ? (
            <p className="waitlist-status" role="status">
              You’re on the list. We’ll be in touch.
            </p>
          ) : (
            <>
              <label className="sr-only" htmlFor="waitlist-email">
                Your email address
              </label>
              <input
                id="waitlist-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                placeholder="Your email address"
                value={email}
                disabled={submitState === 'submitting'}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (submitState === 'error') {
                    setSubmitState('idle');
                  }
                }}
              />
              <button type="submit" disabled={submitState === 'submitting'}>
                {submitState === 'submitting'
                  ? 'Joining…'
                  : 'Join the waitlist'}
              </button>
            </>
          )}
        </form>
        {submitState !== 'joined' && (
          <div className="landing-form-message">
            {submitState === 'error' && (
              <p className="waitlist-status is-error" role="alert">
                Something went wrong. Please try again.
              </p>
            )}
              <p className="landing-fine-print">
                By joining, you agree to our{' '}
                <a
                  href="/privacy"
                  onClick={(event) => {
                    event.preventDefault();
                    openPrivacy();
                  }}
                >
                  Privacy Policy
                </a>
                .
              </p>
          
          </div>
        )}
      </main>

      <div className="landing-phone" aria-hidden="true">
        <div className="phone-screen">
          <img className="phone-art" src={phoneScreen} alt="" />
          <div className="phone-wash" />
          <div className="phone-prompt">
            <span className="phone-mic">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >
                <path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
            </span>
            <span>Describe an app to build…</span>
          </div>
        </div>
        <img className="phone-bezel" src={bezel} alt="" />
      </div>
      <footer className="landing-footer">
        © 2026 Labyrinthine Software Inc. All rights reserved.
      </footer>
      {showPrivacy && <PrivacyPolicy onClose={closePrivacy} />}
    </div>
  );
}
